import { useState } from "react";
import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const PlantCalculator = () => {
  const [area, setArea] = useState<number>(1000);
  const [spacing, setSpacing] = useState<number>(18);

  const spacingInFeet = spacing / 12;
  const sqFtPerPlant = spacingInFeet * spacingInFeet;
  const plantsNeeded =
    area > 0 && spacing > 0 ? Math.ceil(area / sqFtPerPlant) : 0;

  return (
    <Layout>
      <SEO
        title="Plant Calculator | 911 Sourcing"
        description="Calculate exactly how many plants you need for your landscaping project with our easy-to-use plant calculator."
      />

      {/* Hero Section */}
      <section className="relative bg-salt-dark pt-32 pb-20 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tight mb-6">
              Plant <span className="text-plant-lime">Calculator</span>
            </h1>
            <p className="text-xl text-gray-300">
              Quickly determine how much plant material you need for your next
              landscaping project.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-0 items-center">
            {/* Inputs */}
            <div className="p-4 md:p-8 space-y-12">
              <div>
                <h2 className="text-3xl font-black mb-10">Plant Calculator</h2>

                <div className="space-y-10">
                  {/* Total Area */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="text-base font-bold text-gray-900">
                        Total Area
                      </Label>
                      <div className="w-24">
                        <Input
                          type="number"
                          value={area}
                          onChange={(e) => setArea(Number(e.target.value))}
                          className="text-center font-bold"
                        />
                      </div>
                    </div>
                    <Slider
                      value={[area]}
                      onValueChange={(vals) => setArea(vals[0])}
                      max={10000}
                      min={10}
                      step={10}
                      className="py-4 [&_[data-radix-slider-range]]:bg-plant-lime [&_[data-radix-slider-thumb]]:border-gray-200 [&_[data-radix-slider-thumb]]:shadow-md [&_[data-radix-slider-thumb]]:w-6 [&_[data-radix-slider-thumb]]:h-6"
                    />
                    <div className="flex justify-between text-xs text-gray-500 font-medium">
                      <div className="flex flex-col">
                        <span>10</span>
                        <span className="mt-1">Sq Ft</span>
                      </div>
                      <span>10,000</span>
                    </div>
                  </div>

                  {/* Plant Spacing */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="text-base font-bold text-gray-900">
                        Plant Spacing
                      </Label>
                      <div className="w-24">
                        <Input
                          type="number"
                          value={spacing}
                          onChange={(e) => setSpacing(Number(e.target.value))}
                          className="text-center font-bold"
                        />
                      </div>
                    </div>
                    <Slider
                      value={[spacing]}
                      onValueChange={(vals) => setSpacing(vals[0])}
                      max={120}
                      min={1}
                      step={1}
                      className="py-4 [&_[data-radix-slider-range]]:bg-plant-lime [&_[data-radix-slider-thumb]]:border-gray-200 [&_[data-radix-slider-thumb]]:shadow-md [&_[data-radix-slider-thumb]]:w-6 [&_[data-radix-slider-thumb]]:h-6"
                    />
                    <div className="flex justify-between text-xs text-gray-500 font-medium">
                      <div className="flex flex-col">
                        <span>1</span>
                        <span className="mt-1">Inches</span>
                      </div>
                      <span>120</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="bg-black text-white p-8 md:p-12 flex flex-col justify-center rounded-2xl shadow-xl lg:ml-8">
              <div className="mb-12">
                <h3 className="text-xl font-bold mb-2">
                  Estimated Plants Needed
                </h3>
                <div className="text-5xl md:text-6xl font-black mb-2 text-plant-lime">
                  {plantsNeeded.toLocaleString()}
                </div>
                <p className="text-gray-400 text-sm">Based on square spacing</p>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-4">
                  Find your plants today!
                </h3>
                <p className="text-gray-300 mb-8 leading-relaxed text-sm">
                  Now that you know how many plants you need, you need to find
                  them. 911 Sourcing can do that for you!
                </p>
                <Button
                  asChild
                  className="w-full bg-plant-lime hover:bg-plant-lime/90 text-salt-dark py-6 text-lg font-bold"
                >
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PlantCalculator;
